import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { topicProgressApi } from '../lib/api';
import {
  ArrowLeft, BookOpen, CheckCircle2, Bookmark, BookmarkCheck,
  Lightbulb, Brain, Zap, HelpCircle, Trophy, ChevronRight,
  Sparkles, Target, ArrowRight, Clock, Star, Puzzle,
  Image, Loader2, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function StudyMaterialViewer() {
  const { examId, subject, topicId } = useParams();
  const [searchParams] = useSearchParams();
  const topicName = searchParams.get('name') || 'Topic';
  const decodedSubject = decodeURIComponent(subject);
  const { token } = useAuth();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completed, setCompleted] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizRevealed, setQuizRevealed] = useState({});

  useEffect(() => {
    const url = `${API_URL}/study-material/topic/${topicId}?exam_id=${examId}&subject=${encodeURIComponent(decodedSubject)}&topic_name=${encodeURIComponent(topicName)}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setContent(d.content || d))
      .catch(() => toast.error('Failed to load material'))
      .finally(() => setLoading(false));
    // Mark as studied the moment student opens this topic
    topicProgressApi.mark({
      exam_id: examId,
      subject: decodedSubject,
      topic: topicName,
      status: 'studied',
    });
  }, [topicId, examId, decodedSubject, topicName, token]);

  const toggleComplete = async () => {
    const newVal = !completed;
    setCompleted(newVal);
    await fetch(`${API_URL}/study-material/topic/${topicId}/complete`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ exam_id: examId, completed: newVal }),
    });
    toast.success(newVal ? 'Marked as complete!' : 'Unmarked');
  };

  const toggleBookmark = async () => {
    const newVal = !bookmarked;
    setBookmarked(newVal);
    await fetch(`${API_URL}/study-material/topic/${topicId}/bookmark`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ exam_id: examId, bookmarked: newVal }),
    });
    toast.success(newVal ? 'Bookmarked!' : 'Removed bookmark');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <div className="relative">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-700 flex items-center justify-center animate-pulse">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
        </div>
        <p className="text-sm text-muted-foreground animate-pulse">Generating study material with AI...</p>
      </div>
    );
  }

  if (!content || !content.sections) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Failed to load content</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="study-viewer">
      {/* Top Bar */}
      <div className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link
            to={`/study-material/${examId}/${encodeURIComponent(decodedSubject)}`}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="back-to-topics"
          >
            <ArrowLeft className="w-4 h-4" />{decodedSubject}
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" data-testid="go-dashboard-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Home
              </Button>
            </Link>
            <Button variant="ghost" size="sm" onClick={toggleBookmark} data-testid="bookmark-btn"
              className={bookmarked ? 'text-amber-400' : 'text-muted-foreground'}>
              {bookmarked ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
            </Button>
            <Button size="sm" onClick={toggleComplete}
              className={completed ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-primary'}
              data-testid="complete-btn">
              {completed ? <><CheckCircle2 className="w-4 h-4 mr-1" />Completed</> : 'Mark Complete'}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Title */}
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="outline" className="text-xs">{decodedSubject}</Badge>
            {content.estimated_time && (
              <Badge variant="outline" className="text-xs text-muted-foreground">
                <Clock className="w-3 h-3 mr-1" />{content.estimated_time}
              </Badge>
            )}
            {content.difficulty && (
              <Badge className={`text-xs ${content.difficulty === 'Hard' ? 'bg-red-500/10 text-red-400' : content.difficulty === 'Easy' ? 'bg-green-500/10 text-green-400' : 'bg-amber-500/10 text-amber-400'}`}>
                {content.difficulty}
              </Badge>
            )}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">{content.title}</h1>
          {content.subtitle && (
            <p className="text-base text-muted-foreground mt-2 italic">{content.subtitle}</p>
          )}
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {content.sections.map((section, i) => (
            <SectionRenderer key={i} section={section} index={i}
              quizAnswers={quizAnswers} setQuizAnswers={setQuizAnswers}
              quizRevealed={quizRevealed} setQuizRevealed={setQuizRevealed}
              topicId={topicId} token={token} />
          ))}
        </div>

        {/* Exam Tips */}
        {content.exam_tips?.length > 0 && (
          <div className="mt-10 p-6 rounded-2xl border-2 border-dashed border-amber-500/30 bg-amber-500/5" data-testid="exam-tips">
            <h3 className="text-base font-bold flex items-center gap-2 mb-3">
              <Target className="w-5 h-5 text-amber-400" />Exam Tips
            </h3>
            <ul className="space-y-2">
              {content.exam_tips.map((tip, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Star className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Related Topics */}
        {content.related_topics?.length > 0 && (
          <div className="mt-8 p-5 rounded-2xl border border-border bg-card">
            <h3 className="text-sm font-semibold text-muted-foreground mb-3">Related Topics</h3>
            <div className="flex flex-wrap gap-2">
              {content.related_topics.map((t, i) => (
                <Badge key={i} variant="outline" className="text-xs">{t}</Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ========= SECTION RENDERERS ========= */

function SectionRenderer({ section, index, quizAnswers, setQuizAnswers, quizRevealed, setQuizRevealed, topicId, token }) {
  switch (section.type) {
    case 'concept': return <ConceptSection section={section} index={index} />;
    case 'visual': return <VisualSection section={section} />;
    case 'diagram': return <DiagramSection section={section} index={index} topicId={topicId} token={token} />;
    case 'formula': return <FormulaSection section={section} />;
    case 'example': return <ExampleSection section={section} />;
    case 'mnemonics': return <MnemonicSection section={section} />;
    case 'comparison': return <ComparisonSection section={section} />;
    case 'funfact': return <FunFactSection section={section} />;
    case 'quiz': return <QuizSection section={section} answers={quizAnswers} setAnswers={setQuizAnswers}
      revealed={quizRevealed} setRevealed={setQuizRevealed} />;
    default: return null;
  }
}

function ConceptSection({ section, index }) {
  return (
    <div className="relative" data-testid={`section-concept-${index}`}>
      <div className="p-6 rounded-2xl border border-blue-500/20 bg-gradient-to-br from-blue-500/5 to-transparent">
        <h2 className="text-lg font-bold flex items-center gap-2 mb-4">
          <BookOpen className="w-5 h-5 text-blue-400" />{section.title}
        </h2>
        <div className="prose prose-sm dark:prose-invert max-w-none">
          {section.content.split('\n').map((p, i) => (
            <p key={i} className="text-sm leading-relaxed text-foreground/90 mb-3">{p}</p>
          ))}
        </div>
        {section.highlight && (
          <div className="mt-4 p-3 rounded-xl bg-blue-500/10 border border-blue-500/20">
            <p className="text-sm font-medium text-blue-400 flex items-center gap-2">
              <Lightbulb className="w-4 h-4 shrink-0" />{section.highlight}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function DiagramSection({ section, index, topicId, token }) {
  const [imageData, setImageData] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [showDescription, setShowDescription] = useState(true);

  const generateImage = async () => {
    if (!section.image_prompt) return;
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/study-material/topic/${topicId}/generate-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_prompt: section.image_prompt, image_type: `diagram_${index}` }),
      });
      if (!res.ok) throw new Error('Generation failed');
      const data = await res.json();
      setImageData(data.image_base64);
      setShowDescription(false);
      toast.success(data.cached ? 'Loaded diagram' : 'Diagram generated!');
    } catch (e) {
      toast.error('Failed to generate diagram');
    } finally {
      setGenerating(false);
    }
  };

  // Try to load cached image on mount
  useEffect(() => {
    const loadCached = async () => {
      try {
        const res = await fetch(`${API_URL}/study-material/topic/${topicId}/images`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const match = data.images?.find(img => img.image_type === `diagram_${index}`);
          if (match?.image_base64) {
            setImageData(match.image_base64);
            setShowDescription(false);
          }
        }
      } catch {}
    };
    loadCached();
  }, [topicId, index, token]);

  const diagramTypeLabel = {
    flowchart: 'Flowchart', process: 'Process Diagram', anatomy: 'Anatomy Diagram',
    circuit: 'Circuit Diagram', cycle: 'Cycle Diagram', comparison: 'Comparison Chart',
    timeline: 'Timeline', steps: 'Step-by-Step'
  };

  return (
    <div className="rounded-2xl border border-teal-500/20 bg-gradient-to-br from-teal-500/5 to-transparent overflow-hidden" data-testid={`section-diagram-${index}`}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Image className="w-5 h-5 text-teal-400" />{section.title}
          </h2>
          {section.diagram_type && (
            <Badge variant="outline" className="text-[10px] text-teal-400 border-teal-500/30">
              {diagramTypeLabel[section.diagram_type] || section.diagram_type}
            </Badge>
          )}
        </div>

        {/* Description text */}
        {showDescription && section.description && (
          <div className="p-4 rounded-xl bg-teal-500/10 border border-teal-500/20 mb-4">
            <p className="text-sm text-foreground/90 leading-relaxed">{section.description}</p>
          </div>
        )}

        {/* Generated image */}
        {imageData && (
          <div className="relative mb-4">
            <img
              src={`data:image/png;base64,${imageData}`}
              alt={section.title}
              className="w-full rounded-xl border border-border shadow-lg"
              data-testid={`diagram-image-${index}`}
            />
            <button
              onClick={() => setShowDescription(!showDescription)}
              className="absolute top-2 right-2 p-1.5 rounded-lg bg-background/80 backdrop-blur border border-border text-muted-foreground hover:text-foreground transition-colors"
              title={showDescription ? 'Hide description' : 'Show description'}
            >
              <Eye className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Generate button */}
        {!imageData && section.image_prompt && (
          <Button
            onClick={generateImage}
            disabled={generating}
            className="w-full bg-teal-600 hover:bg-teal-500 text-white"
            data-testid={`generate-diagram-btn-${index}`}
          >
            {generating ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating AI Diagram... (may take ~30s)</>
            ) : (
              <><Image className="w-4 h-4 mr-2" />Generate AI Diagram</>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

function VisualSection({ section }) {
  const nodes = section.nodes || [];
  const root = nodes[0];
  if (!root) return null;

  return (
    <div className="p-6 rounded-2xl border border-violet-500/20 bg-gradient-to-br from-violet-500/5 to-transparent" data-testid="section-visual">
      <h2 className="text-lg font-bold flex items-center gap-2 mb-5">
        <Brain className="w-5 h-5 text-violet-400" />{section.title}
      </h2>

      {/* Mind Map / Flowchart */}
      <div className="flex flex-col items-center">
        {/* Root Node */}
        <div className="px-5 py-3 rounded-2xl bg-gradient-to-r from-violet-500 to-purple-600 text-white font-bold text-sm shadow-lg">
          {root.label}
        </div>

        {root.children && root.children.length > 0 && (
          <>
            <div className="w-px h-6 bg-violet-500/40" />
            <div className="flex flex-wrap justify-center gap-3 max-w-full">
              {root.children.map((child, i) => {
                const childNode = nodes.find(n => n.label === child);
                return (
                  <div key={i} className="flex flex-col items-center">
                    <div className="w-px h-4 bg-violet-500/30" />
                    <div className="px-4 py-2.5 rounded-xl border border-violet-500/30 bg-violet-500/10 text-center max-w-[180px]">
                      <p className="text-sm font-semibold text-violet-300">{child}</p>
                      {childNode?.detail && (
                        <p className="text-[10px] text-muted-foreground mt-1">{childNode.detail}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function FormulaSection({ section }) {
  return (
    <div className="p-6 rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/5 to-transparent" data-testid="section-formula">
      <h2 className="text-lg font-bold flex items-center gap-2 mb-4">
        <Zap className="w-5 h-5 text-emerald-400" />{section.title}
      </h2>
      <div className="space-y-3">
        {(section.items || []).map((item, i) => (
          <div key={i} className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
            <code className="text-base font-bold text-emerald-300 block mb-2">{item.formula}</code>
            {item.meaning && <p className="text-xs text-muted-foreground mb-1">{item.meaning}</p>}
            {item.when_to_use && (
              <p className="text-xs text-emerald-400/70 flex items-center gap-1 mt-2">
                <ArrowRight className="w-3 h-3" />{item.when_to_use}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ExampleSection({ section }) {
  const [expandedIdx, setExpandedIdx] = useState(null);

  return (
    <div className="p-6 rounded-2xl border border-orange-500/20 bg-gradient-to-br from-orange-500/5 to-transparent" data-testid="section-example">
      <h2 className="text-lg font-bold flex items-center gap-2 mb-4">
        <Puzzle className="w-5 h-5 text-orange-400" />{section.title}
      </h2>
      <div className="space-y-4">
        {(section.examples || []).map((ex, i) => (
          <div key={i} className="rounded-xl border border-orange-500/20 overflow-hidden">
            <button
              onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}
              className="w-full text-left p-4 hover:bg-orange-500/5 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <Badge className={`text-[10px] mb-2 ${ex.difficulty === 'Hard' ? 'bg-red-500/10 text-red-400' : ex.difficulty === 'Easy' ? 'bg-green-500/10 text-green-400' : 'bg-amber-500/10 text-amber-400'}`}>
                    {ex.difficulty}
                  </Badge>
                  <p className="text-sm font-medium">{ex.problem}</p>
                </div>
                <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform shrink-0 mt-1 ${expandedIdx === i ? 'rotate-90' : ''}`} />
              </div>
            </button>
            {expandedIdx === i && (
              <div className="px-4 pb-4 border-t border-orange-500/10 pt-3 space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground font-semibold mb-2 uppercase tracking-wider">Solution</p>
                  {(ex.solution_steps || []).map((step, si) => (
                    <div key={si} className="flex items-start gap-2 mb-2">
                      <span className="w-5 h-5 rounded-full bg-orange-500/20 flex items-center justify-center text-[10px] font-bold text-orange-400 shrink-0 mt-0.5">
                        {si + 1}
                      </span>
                      <p className="text-sm text-foreground/90">{step}</p>
                    </div>
                  ))}
                </div>
                {ex.answer && (
                  <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
                    <p className="text-sm font-bold text-orange-300">Answer: {ex.answer}</p>
                  </div>
                )}
                {ex.trick && (
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <p className="text-xs text-amber-400 flex items-center gap-1.5">
                      <Lightbulb className="w-3.5 h-3.5" />Pro Tip: {ex.trick}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function MnemonicSection({ section }) {
  return (
    <div className="p-6 rounded-2xl border border-pink-500/20 bg-gradient-to-br from-pink-500/5 to-transparent" data-testid="section-mnemonics">
      <h2 className="text-lg font-bold flex items-center gap-2 mb-4">
        <Brain className="w-5 h-5 text-pink-400" />{section.title}
      </h2>
      <div className="space-y-3">
        {(section.items || []).map((item, i) => (
          <div key={i} className="p-4 rounded-xl bg-pink-500/10 border border-pink-500/20">
            <p className="text-base font-bold text-pink-300 mb-1">{item.mnemonic}</p>
            <p className="text-xs text-muted-foreground">{item.helps_remember}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ComparisonSection({ section }) {
  return (
    <div className="p-6 rounded-2xl border border-cyan-500/20 bg-gradient-to-br from-cyan-500/5 to-transparent" data-testid="section-comparison">
      <h2 className="text-lg font-bold flex items-center gap-2 mb-4">
        <HelpCircle className="w-5 h-5 text-cyan-400" />{section.title}
      </h2>
      <div className="space-y-4">
        {(section.comparisons || []).map((comp, i) => (
          <div key={i} className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-center">
              <p className="text-sm font-bold text-cyan-300">{comp.item_a}</p>
            </div>
            <div className="p-3 rounded-xl bg-violet-500/10 border border-violet-500/20 text-center">
              <p className="text-sm font-bold text-violet-300">{comp.item_b}</p>
            </div>
            <div className="col-span-2 p-3 rounded-xl bg-red-500/5 border border-red-500/10">
              <p className="text-xs"><span className="font-semibold text-red-400">Difference: </span>{comp.difference}</p>
            </div>
            {comp.similarity && (
              <div className="col-span-2 p-3 rounded-xl bg-green-500/5 border border-green-500/10">
                <p className="text-xs"><span className="font-semibold text-green-400">Similarity: </span>{comp.similarity}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function FunFactSection({ section }) {
  return (
    <div className="p-6 rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-500/5 to-transparent" data-testid="section-funfact">
      <h2 className="text-lg font-bold flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-amber-400" />{section.title}
      </h2>
      <div className="space-y-3">
        {(section.facts || []).map((fact, i) => (
          <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
            <Star className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-sm text-foreground/90">{fact}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function QuizSection({ section, answers, setAnswers, revealed, setRevealed }) {
  const questions = section.questions || [];
  const allAnswered = questions.every((_, i) => answers[i] !== undefined);
  const correctCount = questions.filter((q, i) => answers[i] === q.correct).length;

  return (
    <div className="p-6 rounded-2xl border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent" data-testid="section-quiz">
      <h2 className="text-lg font-bold flex items-center gap-2 mb-5">
        <Trophy className="w-5 h-5 text-primary" />{section.title}
      </h2>
      <div className="space-y-5">
        {questions.map((q, qi) => {
          const answered = answers[qi] !== undefined;
          const isRevealed = revealed[qi];
          // Phase 40.23 — accept either `correct_answer` (fallback bank) or
          // `correct` (legacy question_bank) so the marker never wrongly
          // reports the user's right answer as wrong.
          const correctIdx = q.correct_answer ?? q.correct;
          const isCorrect = answers[qi] === correctIdx;

          return (
            <div key={qi} className="p-4 rounded-xl border border-border bg-card/50">
              <p className="text-sm font-medium mb-3">Q{qi + 1}. {q.question}</p>
              <div className="space-y-2">
                {(q.options || []).map((opt, oi) => {
                  let cls = 'border-border hover:border-primary/30 cursor-pointer';
                  if (answered && isRevealed) {
                    if (oi === correctIdx) cls = 'border-emerald-500 bg-emerald-500/10';
                    else if (oi === answers[qi] && !isCorrect) cls = 'border-red-500 bg-red-500/10';
                    else cls = 'border-border opacity-50';
                  } else if (answers[qi] === oi) {
                    cls = 'border-primary bg-primary/10';
                  }
                  return (
                    <button
                      key={oi}
                      onClick={() => !isRevealed && setAnswers(p => ({ ...p, [qi]: oi }))}
                      className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm transition-all ${cls}`}
                      disabled={isRevealed}
                      data-testid={`quiz-q${qi}-opt${oi}`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
              {answered && !isRevealed && (
                <Button size="sm" variant="outline" className="mt-3 text-xs"
                  onClick={() => setRevealed(p => ({ ...p, [qi]: true }))}>
                  Check Answer
                </Button>
              )}
              {isRevealed && (
                <div className={`mt-3 p-3 rounded-lg text-xs ${isCorrect ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                  {isCorrect ? 'Correct!' : 'Incorrect.'} {q.explanation}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {allAnswered && Object.keys(revealed).length === questions.length && (
        <div className="mt-5 p-4 rounded-xl bg-primary/10 border border-primary/20 text-center">
          <p className="text-lg font-bold">{correctCount}/{questions.length} Correct</p>
          <p className="text-xs text-muted-foreground mt-1">
            {correctCount === questions.length ? 'Perfect score! You nailed it!' : 'Keep practicing, you\'re getting there!'}
          </p>
        </div>
      )}
    </div>
  );
}
